import { useEffect, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { BudgetEditorDialog } from "@/pages/FinanceBudgetDocumentsView";

// DOC-03: las series/versiones contractuales se crean desde el contrato,
// vinculadas a una instalación del mismo. Cada carga crea un objeto y una fila;
// nunca sobrescribe el contrato firmado ni usa la subida genérica de instalación.
// 2) "Presupuesto aceptado" → BudgetEditorDialog (flujo Finanzas existente
//    preview/review/confirm). Sin APIs nuevas ni segundo registro de storage.
//
// Fail-closed: el componente solo se renderiza con capacidades de escritura
// ya autorizadas por customers.contracts.getMyCapabilities; el backend vuelve
// a comprobarlas para cada endpoint.

const MAX_DOCUMENT_FILE_BYTES = 10 * 1024 * 1024;

type ContractDocumentKind = "contract" | "annex";

interface ContractDocumentVersionTarget {
  id: number;
  name: string;
  installationId: number;
  seriesId: number | null;
  versionKind: ContractDocumentKind | null;
  isContractSigned?: boolean;
  origin: string | null;
}

interface ContractDocumentActionsProps {
  contractId: number;
  canWriteCommercial: boolean;
  canWriteEconomic: boolean;
  versionTarget?: ContractDocumentVersionTarget | null;
  onVersionTargetHandled?: () => void;
}

function installationLabel(item: {
  installationCode: string | null;
  clientId: string | null;
  name: string | null;
  workDescription: string | null;
  siteName: string | null;
}): string {
  const code = item.installationCode ?? item.clientId ?? "Instalación";
  const description = item.name ?? item.workDescription ?? item.siteName;
  return description ? `Exp. ${code} · ${description}` : `Exp. ${code}`;
}

export function ContractDocumentActions({
  contractId,
  canWriteCommercial,
  canWriteEconomic,
  versionTarget = null,
  onVersionTargetHandled,
}: ContractDocumentActionsProps) {
  const utils = trpc.useUtils();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [installationId, setInstallationId] = useState("");
  const [documentType, setDocumentType] = useState<ContractDocumentKind>("contract");
  const [file, setFile] = useState<File | null>(null);

  const canWriteDocuments = canWriteCommercial;

  const installationsQuery = trpc.customers.contracts.listInstallations.useQuery(
    { contractId },
    {
      enabled: contractId > 0 && ((canWriteCommercial && (uploadOpen || versionTarget !== null)) || (canWriteEconomic && budgetOpen)),
    }
  );

  const installations = installationsQuery.data ?? [];

  const uploadMutation = trpc.customers.contracts.addDocumentVersion.useMutation();

  useEffect(() => {
    if (!versionTarget) return;
    setDocumentType(
      versionTarget.versionKind ?? (versionTarget.isContractSigned ? "contract" : "annex")
    );
    setFile(null);
  }, [versionTarget]);

  if ((!canWriteDocuments && !canWriteEconomic) || contractId <= 0) {
    return null;
  }

  const closeUploadDialog = () => {
    setUploadOpen(false);
    setInstallationId("");
    setDocumentType("contract");
    setFile(null);
    onVersionTargetHandled?.();
  };

  const handleUploadSubmit = () => {
    if (!versionTarget && !installationId) {
      toast.error("Selecciona la instalación del contrato");
      return;
    }
    if (!file) {
      toast.error("Selecciona un archivo");
      return;
    }
    if (file.size > MAX_DOCUMENT_FILE_BYTES) {
      toast.error("El archivo supera el máximo de 10 MB");
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => toast.error("No se pudo leer el archivo");
    reader.onload = async () => {
      const result = reader.result;
      if (typeof result !== "string") {
        toast.error("No se pudo leer el archivo");
        return;
      }
      const fileData = result.split(",")[1] || "";
      const common = {
        contractId,
        name: file.name,
        fileData,
        mimeType: file.type || "application/octet-stream",
      };
      const payload = versionTarget?.seriesId != null
        ? { ...common, mode: "series" as const, seriesId: versionTarget.seriesId }
        : versionTarget
          ? {
              ...common,
              mode: "legacy" as const,
              baseDocumentId: versionTarget.id,
              kind: documentType,
            }
          : {
              ...common,
              mode: "new" as const,
              installationId: Number(installationId),
              kind: documentType,
            };
      const affectedInstallationId = versionTarget?.installationId ?? Number(installationId);
      try {
        await uploadMutation.mutateAsync(payload);
        toast.success(versionTarget ? "Nueva versión guardada y vigente" : "Serie documental creada");
        await Promise.all([
          utils.customers.contracts.listDocuments.invalidate({ contractId }),
          utils.customers.contracts.listDocumentVersions.invalidate({ contractId }),
          utils.customers.contracts.listArchivedDocuments.invalidate({ contractId }),
          utils.documents.listByInstallation.invalidate({ installationId: affectedInstallationId }),
        ]);
        closeUploadDialog();
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "No se pudo guardar la versión documental"
        );
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {canWriteCommercial && (
        <Button
          size="sm"
          className="min-h-[44px]"
          onClick={() => setUploadOpen(true)}
        >
          <Upload className="mr-1 h-4 w-4" aria-hidden="true" />
          Subir documento
        </Button>
      )}
      {canWriteEconomic && (
        <Button
          size="sm"
          variant="outline"
          className="min-h-[44px]"
          onClick={() => setBudgetOpen(true)}
        >
          Subir presupuesto aceptado
        </Button>
      )}

      <Dialog
        open={uploadOpen || versionTarget !== null}
        onOpenChange={open => !open && closeUploadDialog()}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {versionTarget ? "Nueva versión del documento" : "Añadir documento al contrato"}
            </DialogTitle>
            <DialogDescription>
              {versionTarget?.seriesId != null
                ? `La carga será la versión vigente nueva de «${versionTarget.name}»; las anteriores seguirán en el historial.`
                : versionTarget
                  ? `Se vinculará «${versionTarget.name}» explícitamente como versión 1 y el archivo nuevo será la versión 2. No se modifica el original ni sus referencias.`
                  : "Se creará una nueva serie y su primera versión vigente. Selecciona una instalación vinculada al contrato. Máximo 10 MB."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            {versionTarget ? (
              <p className="rounded-md border bg-muted/30 p-3 text-sm">
                Instalación: {versionTarget.origin ?? `#${versionTarget.installationId}`}
              </p>
            ) : (
              <div className="space-y-2">
                <Label>Instalación del contrato *</Label>
                <Select
                  value={installationId}
                  onValueChange={setInstallationId}
                  disabled={installationsQuery.isLoading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecciona una instalación" />
                  </SelectTrigger>
                  <SelectContent>
                    {installations.map(item => (
                      <SelectItem
                        key={item.installationId}
                        value={String(item.installationId)}
                      >
                        {installationLabel(item)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {versionTarget?.seriesId != null ? (
              <p className="rounded-md border bg-muted/30 p-3 text-sm">
                Clase de serie: {versionTarget.versionKind === "contract" ? "Contrato" : "Anexo contractual"}
              </p>
            ) : (
              <div className="space-y-2">
                <Label>Clase documental *</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button
                  type="button"
                  variant={documentType === "contract" ? "default" : "outline"}
                  aria-pressed={documentType === "contract"}
                  className="h-auto whitespace-normal py-2 text-xs"
                  onClick={() => setDocumentType("contract")}
                >
                  Contrato
                </Button>
                <Button
                  type="button"
                  variant={documentType === "annex" ? "default" : "outline"}
                  aria-pressed={documentType === "annex"}
                  className="h-auto whitespace-normal py-2 text-xs"
                  onClick={() => setDocumentType("annex")}
                >
                  Anexo contractual
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {documentType === "contract"
                  ? "La clase queda guardada en la relación contractual; el registro y los bytes anteriores no se sobrescriben."
                  : "El anexo se clasifica explícitamente, sin depender del nombre del archivo."}
              </p>
            </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="contract-document-file">Archivo *</Label>
              <Input
                id="contract-document-file"
                type="file"
                onChange={event => setFile(event.target.files?.[0] ?? null)}
              />
              {file ? (
                <p className="truncate text-xs text-muted-foreground">
                  {file.name} · {(file.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeUploadDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleUploadSubmit}
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : null}
              {versionTarget ? "Subir nueva versión" : "Crear serie documental"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {budgetOpen && (
        <BudgetEditorDialog
          key={`contract-budget-${contractId}-${installations.length}`}
          open
          mode="upload"
          installations={installations.map(item => ({
            id: item.installationId,
            clientId: item.clientId,
            clientName: item.name ?? item.siteName,
            workDescription: item.workDescription,
          }))}
          initialInstallationId={installations[0]?.installationId ?? null}
          onOpenChange={open => !open && setBudgetOpen(false)}
          onSaved={async () => {
            setBudgetOpen(false);
            // El puente de documentos del contrato incluye el presupuesto
            // aceptado vía la instalación vinculada.
            await utils.customers.contracts.listDocuments.invalidate({ contractId });
            await utils.finance.budgetDocuments.list.invalidate();
          }}
        />
      )}
    </div>
  );
}
